<template>
  <div ref="container" class="route-card-map" role="img" :aria-label="t('route_cards.map_label')"></div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import mapboxgl from 'mapbox-gl';
import { HiikerMapStyle } from '../../utils/models/MapStyles';
import { darkenHex, getRouteStyleSettings } from '../../utils/routeStyleSettings';
import { routeBounds, snapToRoute, type RoutePosition } from '../../utils/route_cards/routeLine';
import { dragRange, type EditableCheckpoint } from '../../utils/route_cards/checkpoints';
import { routeCardMapFeatures } from '../../utils/route_cards/mapFeatures';
import { ensureSectionPillIcons, SECTION_PILL_PREFIX } from '../../utils/route_cards/sectionPillIcon';
import { ensureStageMarkerIcons, STAGE_MARKER_ICON_PREFIX } from '../hike_map/helpers/stageMarkerIcon';

// The route card's map, drawn the way the hike map draws a hike: the route in
// the user's own main-route style, the map style's start and finish icons, and
// the hike map's yellow numbered discs for the points between. Each section
// gets a small "0–1" pill half way along it — the same label as the table and
// the PDF — which gives way to the points where they crowd.
//
// Everything is a symbol layer, so Mapbox handles the overlaps. In the editor
// a point between start and finish drags along the route: the drag is snapped
// onto the line between the point's two neighbours as it moves.
const props = defineProps<{
  route: RoutePosition[];
  points: EditableCheckpoint[];
  selected: number;
  editable: boolean;
}>();

const emit = defineEmits<{
  (event: 'select', index: number): void;
  (event: 'move', index: number, distance: number): void;
}>();

const { t } = useI18n();

const MAPBOX_TOKEN = 'pk.eyJ1IjoiaGlpa2VyLW1hcHMiLCJhIjoiY2x0YThyZHppMWhmejJqbzExODBuY3dveiJ9.nBrtnXgbzxpAx_953HCB-w';
const SOURCES = {
  route: 'route-card-route',
  ends: 'route-card-ends',
  points: 'route-card-points',
  sections: 'route-card-sections',
  selection: 'route-card-selection',
} as const;
const START_ICON = 'hike-control-point-start';
const FINISH_ICON = 'hike-control-point-end';
// The hike map's start/finish icon sizing, so the two maps match at every zoom.
const END_ICON_SIZE: any = ['interpolate', ['linear'], ['zoom'], 0, 0.1, 9, 0.2, 12, 0.3, 22, 0.5];

const container = ref<HTMLDivElement | null>(null);
let map: mapboxgl.Map | null = null;
let loaded = false;
let userMoved = false;
let resizeObserver: ResizeObserver | null = null;
// While a point is being dragged, where it is now — drawn in place of its stored distance.
let drag: { index: number; distance: number; moved: boolean } | null = null;

function currentPoints(): EditableCheckpoint[] {
  if (!drag) return props.points;
  const { index, distance } = drag;
  return props.points.map((point, i) => (i === index ? { ...point, distanceMeters: distance } : point));
}

function setData(id: string, data: unknown): void {
  (map?.getSource(id) as mapboxgl.GeoJSONSource | undefined)?.setData(data as any);
}

function render(): void {
  if (!map || !loaded) return;
  const features = routeCardMapFeatures(props.route, currentPoints(), props.selected);
  ensureStageMarkerIcons(map, features.points.features.map((f) => f.properties.number));
  ensureSectionPillIcons(map, features.sections.features.map((f) => String(f.properties.label)));
  setData(SOURCES.ends, features.ends);
  setData(SOURCES.points, features.points);
  setData(SOURCES.sections, features.sections);
  setData(SOURCES.selection, features.selection);
}

function routeData() {
  return {
    type: 'Feature',
    properties: {},
    geometry: { type: 'LineString', coordinates: props.route.map(([lng, lat]) => [lng, lat]) },
  };
}

const emptyCollection = { type: 'FeatureCollection', features: [] };

function addLayers(m: mapboxgl.Map): void {
  const main = getRouteStyleSettings().main;
  Object.values(SOURCES).forEach((id) => {
    m.addSource(id, { type: 'geojson', data: (id === SOURCES.route ? routeData() : emptyCollection) as any });
  });

  m.addLayer({
    id: 'route-card-route-border',
    type: 'line',
    source: SOURCES.route,
    layout: { 'line-join': 'round', 'line-cap': 'round' },
    paint: { 'line-color': darkenHex(main.color), 'line-width': 9 * main.thickness, 'line-opacity': main.opacity / 100 },
  });
  m.addLayer({
    id: 'route-card-route',
    type: 'line',
    source: SOURCES.route,
    layout: { 'line-join': 'round', 'line-cap': 'round' },
    paint: { 'line-color': main.color, 'line-width': 5 * main.thickness, 'line-opacity': main.opacity / 100 },
  });

  // Below the markers: Mapbox places the upper layers' symbols first, so a pill
  // that would cover a point is the one that's hidden.
  m.addLayer({
    id: 'route-card-sections',
    type: 'symbol',
    source: SOURCES.sections,
    layout: {
      'icon-image': ['concat', SECTION_PILL_PREFIX, ['get', 'label'], ['case', ['get', 'selected'], '-selected', '']],
      'icon-allow-overlap': false,
      'icon-padding': 2,
      'symbol-sort-key': ['case', ['get', 'selected'], 0, 1],
    },
  });

  m.addLayer({
    id: 'route-card-selection',
    type: 'circle',
    source: SOURCES.selection,
    filter: ['!', ['get', 'end']],
    paint: {
      'circle-radius': 17,
      'circle-color': '#FFFFFF',
      'circle-stroke-color': '#17181D',
      'circle-stroke-width': 1.5,
    },
  });

  m.addLayer({
    id: 'route-card-points',
    type: 'symbol',
    source: SOURCES.points,
    layout: {
      'icon-image': ['concat', STAGE_MARKER_ICON_PREFIX, ['to-string', ['get', 'number']]],
      'icon-size': ['case', ['get', 'selected'], 1.15, 1],
      'icon-allow-overlap': true,
      'symbol-sort-key': ['case', ['get', 'selected'], 1, 0],
    },
  });

  m.addLayer({
    id: 'route-card-ends',
    type: 'symbol',
    source: SOURCES.ends,
    layout: {
      'icon-image': ['match', ['get', 'end'], 'start', START_ICON, FINISH_ICON],
      'icon-size': END_ICON_SIZE,
      'icon-anchor': 'bottom',
      'icon-allow-overlap': true,
      // The finish above the start on a loop, as on the hike map.
      'symbol-sort-key': ['match', ['get', 'end'], 'finish', 1, 0],
    },
  });
}

// The start and finish icons come with HiiKER's map styles. Should a style
// ever lack one, draw a plain disc in its place rather than nothing.
function drawFallbackEnd(m: mapboxgl.Map, id: string): void {
  const size = 40;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2 - 3, 0, Math.PI * 2);
  ctx.fillStyle = id === START_ICON ? '#4BC263' : '#DB2B35';
  ctx.fill();
  ctx.lineWidth = 4;
  ctx.strokeStyle = '#FFFFFF';
  ctx.stroke();
  m.addImage(id, ctx.getImageData(0, 0, size, size), { pixelRatio: 0.6 });
}

function frame(): void {
  const bounds = routeBounds(props.route);
  if (!map || !bounds) return;
  map.resize();
  map.fitBounds(bounds, { padding: 40, maxZoom: 15, duration: 0 });
}

const featureIndex = (event: any): number | null => {
  const index = event.features?.[0]?.properties?.index;
  return typeof index === 'number' ? index : null;
};

function onDragMove(event: any): void {
  if (!drag || !map) return;
  const { lng, lat } = event.lngLat;
  drag.distance = snapToRoute(props.route, lng, lat, dragRange(props.points, drag.index));
  drag.moved = true;
  render();
}

function endDrag(): void {
  if (!drag || !map) return;
  const { index, distance, moved } = drag;
  drag = null;
  map.off('mousemove', onDragMove);
  map.off('touchmove', onDragMove);
  map.dragPan.enable();
  map.getCanvas().style.cursor = '';
  if (moved) emit('move', index, distance); else emit('select', index);
}

function startDrag(event: any): void {
  const index = featureIndex(event);
  if (!map || !props.editable || index === null) return;
  if (event.originalEvent?.touches && event.originalEvent.touches.length !== 1) return;
  event.preventDefault();
  drag = { index, distance: props.points[index].distanceMeters, moved: false };
  map.dragPan.disable();
  map.getCanvas().style.cursor = 'grabbing';
  map.on('mousemove', onDragMove);
  map.on('touchmove', onDragMove);
  map.once('mouseup', endDrag);
  map.once('touchend', endDrag);
}

function wireInteractions(m: mapboxgl.Map): void {
  m.on('mousedown', 'route-card-points', startDrag);
  m.on('touchstart', 'route-card-points', startDrag);

  // A tap on the finish or a section pill picks that section; the start has no
  // section of its own to pick.
  const select = (event: any) => {
    const index = featureIndex(event);
    if (index !== null && index >= 1) emit('select', index);
  };
  m.on('click', 'route-card-ends', select);
  m.on('click', 'route-card-sections', select);
  // Without the editor's drag, a point is just tapped.
  m.on('click', 'route-card-points', (event: any) => { if (!props.editable) select(event); });

  const pointer = (cursor: string) => () => { if (!drag) m.getCanvas().style.cursor = cursor; };
  ['route-card-ends', 'route-card-sections'].forEach((layer) => {
    m.on('mouseenter', layer, pointer('pointer'));
    m.on('mouseleave', layer, pointer(''));
  });
  m.on('mouseenter', 'route-card-points', () => pointer(props.editable ? 'grab' : 'pointer')());
  m.on('mouseleave', 'route-card-points', pointer(''));

  m.on('dragstart', () => { userMoved = true; });
  m.on('wheel', () => { userMoved = true; });
}

onMounted(() => {
  if (!container.value) return;
  mapboxgl.accessToken = MAPBOX_TOKEN;
  map = new mapboxgl.Map({
    container: container.value,
    style: HiikerMapStyle.standard().styleUrl,
    attributionControl: true,
  });
  map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
  map.on('styleimagemissing', (event: any) => {
    if (map && (event.id === START_ICON || event.id === FINISH_ICON) && !map.hasImage(event.id)) {
      drawFallbackEnd(map, event.id);
    }
  });
  map.on('load', () => {
    if (!map) return;
    loaded = true;
    addLayers(map);
    wireInteractions(map);
    frame();
    render();
  });

  // The panel settles its size after the map is created (the page's flex
  // layout wraps once the table arrives), so frame the route again once it
  // has — until the user has moved the map themselves.
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => {
      if (!loaded) return;
      if (userMoved) map?.resize(); else frame();
    });
    resizeObserver.observe(container.value);
  }
});

watch(() => [props.points, props.selected, props.editable], () => {
  if (!drag) render();
}, { deep: true });

watch(() => props.route, () => {
  if (!map || !loaded) return;
  setData(SOURCES.route, routeData());
  frame();
  render();
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  map?.remove();
  map = null;
});
</script>

<style lang="scss">
.route-card-map {
  width: 100%;
  height: 520px;
  background: #1F2126;

  @media (max-width: 768px) {
    height: 360px;
  }
}
</style>
